import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { GlossaryDetailPage } from "@/components/calv/glossary/GlossaryDetailPage";
import { GlossaryNotFound } from "@/components/calv/glossary/GlossaryNotFound";
import type { GlossaryEntry } from "@/lib/glossary";
import { getGlossaryDetail, type GlossaryDetail } from "@/lib/glossary-content";
import { SITE_URL, pathMap } from "@/lib/locale";
import { pageHead } from "@/lib/seo";

type LoaderData = {
  entry: GlossaryEntry;
  detail: GlossaryDetail | null;
  alternateLocalePath: string;
  alternateSlug: string | null;
};

export const Route = createFileRoute("/glossar/$slug")({
  // C3: glossary.ts is loaded dynamically inside the loader so the 86 KiB
  // GLOSSARY array never reaches the main bundle. Slug-shape validation and
  // tier-2 redirect — previously in beforeLoad — now happen here so the
  // module stays out of the critical path.
  loader: async ({ params }): Promise<LoaderData> => {
    if (
      params.slug.startsWith("$") ||
      params.slug.startsWith("%24") ||
      params.slug === "[slug]"
    ) {
      throw redirect({ to: "/glossar", statusCode: 301 });
    }
    const { getGlossaryEntryBySlug, getGlossaryAlternateSlug } = await import(
      "@/lib/glossary"
    );
    const entry = getGlossaryEntryBySlug(params.slug, "de");
    if (!entry) throw notFound();
    if (entry.tier === 2) {
      throw redirect({ to: "/glossar", hash: entry.id });
    }
    const alternateSlug = getGlossaryAlternateSlug(entry, "en");
    const alternateLocalePath = alternateSlug
      ? `${pathMap.glossary.en}/${alternateSlug}`
      : pathMap.glossary.en;
    const detail = await getGlossaryDetail(entry.id, "de");
    return { entry, detail, alternateLocalePath, alternateSlug };
  },
  head: ({ loaderData }) => {
    const entry = loaderData?.entry;
    if (!entry || entry.tier !== 1) {
      const h = pageHead("glossary", "de");
      return { meta: h.meta, links: h.links };
    }
    const detail = loaderData?.detail;
    const enSlug = loaderData?.alternateSlug;
    const canonical = `${SITE_URL}${pathMap.glossary.de}/${entry.slug.de}`;
    const altEn = enSlug
      ? `${SITE_URL}${pathMap.glossary.en}/${enSlug}`
      : `${SITE_URL}${pathMap.glossary.en}`;
    const title = detail?.fm.seoTitle ?? `${entry.term.de} — Glossar — Calvarius`;
    const description = detail?.fm.metaDescription ?? entry.shortDefinition.de;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "de_DE" },
        { property: "og:locale:alternate", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "de", href: canonical },
        { rel: "alternate", hrefLang: "en", href: altEn },
        { rel: "alternate", hrefLang: "x-default", href: canonical },
      ],
    };
  },
  component: GlossaryRoute,
  notFoundComponent: () => <GlossaryNotFound locale="de" />,
});

function GlossaryRoute() {
  const { entry, detail } = Route.useLoaderData();
  if (!detail) throw notFound();
  return <GlossaryDetailPage entry={entry} detail={detail} locale="de" />;
}
